import { z } from "zod";

import { CeStructureSchema } from "api/schemas/Patients";
import { LateralLocationSchema, LocationSchema } from "api/schemas/shared/Treatment";
import { MediumSchema, PurposeSchema, StateSchema } from "api/services/appointment/schemas/shared";

const PinnedMessagesSchema = z.array(
  z.object({
    body: z.string(),
    created_at: z.string(),
    id: z.number(),
    read_at: z.string().nullable(),
  })
);
export type PinnedMessages = z.infer<typeof PinnedMessagesSchema>;

const ConversationListItemSchema = z.object({
  has_unread_messages: z.boolean(),
  last_message: z
    .object({
      body: z.string().nullable(),
      created_at: z.string(),
      inbound: z.boolean(),
      read_at: z.string().nullable(),
    })
    .nullable(),
  peer: z.object({
    avatars: z.object({
      large: z.string().nullable(),
      medium: z.string().nullable(),
      small: z.string().nullable(),
    }),
    first_name: z.string().nullable(),
    id: z.number(),
    last_name: z.string().nullable(),
    preferred_name: z.string().nullable(),
    premium_type: z.string(),
    therapist_assignment_role: z.enum(["main_therapist", "substitute_therapist"]).nullable(),
  }),
  pinned_messages: PinnedMessagesSchema,
});
export type ConversationListItem = z.infer<typeof ConversationListItemSchema>;

export const ConversationsResponseSchema = z.object({
  conversations: z.array(ConversationListItemSchema),
  unreadCount: z.number(),
});
export type ConversationsResponse = z.infer<typeof ConversationsResponseSchema>;

const UserSchema = z.object({
  avatar: z.string().nullable(),
  first_name: z.string().nullable(),
  id: z.number(),
  last_name: z.string().nullable(),
  preferred_name: z.string().nullable(),
});

const IncludedActivitySchema = z.object({
  activity_name: z.string(),
  activity_type: z.string(),
  blocking: z.boolean(),
  comment: z.string().nullable(),
  completed_on: z.string().nullable(),
  day_index: z.number().nullable(),
  duration_minutes: z.number().nullable(),
  enabled: z.boolean(),
  exercise: z
    .object({
      current_level: z.number().nullable(),
      exercise_number: z.number().nullable(),
      id: z.number().nullable(),
      level: z.number().nullable(),
      levels: z
        .array(
          z.object({
            id: z.number(),
            level: z.number(),
            reps: z.string(),
            sets: z.number(),
          })
        )
        .nullable(),
      name: z.string().nullable(),
      new: z.boolean().nullable(),
      premium: z.boolean().nullable(),
    })
    .nullable()
    .optional(),
  id: z.number(),
  name: z.string(),
  required: z.boolean().nullable(),
  start_date: z.string().nullable(),
  status: z.string().nullable(),
  translations: z
    .object({
      library_id: z.number().nullable().optional(),
      title: z.string().nullable().optional(),
    })
    .nullable(),
  viewed_by_therapist_at: z.string().nullable(),
  week_day: z.number().nullable(),
  week_number: z.number().nullable(),
});

const IncludedAppointmentSchema = z.object({
  end_time: z.string(),
  ics_url: z.string().nullable(),
  id: z.number(),
  location: z.string().nullable(),
  medium: MediumSchema,
  patient_first_name: z.string().nullable(),
  patient_id: z.number(),
  patient_last_name: z.string().nullable(),
  patient_number: z.string(),
  patient_preferred_name: z.string().nullable(),
  purpose: PurposeSchema,
  start_time: z.string(),
  state: StateSchema.nullable(),
  state_v2: StateSchema.nullable(),
});
export type IncludedAppointment = z.infer<typeof IncludedAppointmentSchema>;

const MessageDataSchema = z.object({
  assignment_end_date: z.string().nullable(),
  assignment_role: z.string(),
  change: z.string(),
  previously_active_therapist: z.object({
    first_name: z.string().nullable(),
    id: z.number(),
    last_name: z.string().nullable(),
  }),
});
export type MessageData = z.infer<typeof MessageDataSchema>;

const MessageType = z.enum([
  "activity",
  "appointment",
  "insurance_change",
  "guided_care_started",
  "patient_assignment_change",
  "patient_end_treatment",
  "pt_on_demand_payment_success",
  "pt_on_demand_started",
  "pt_on_demand_ended",
  "suggested_kickoff_appointment",
  "switched_treatment",
  "text",
]);

const MessageSchema = z.object({
  body: z.string().nullable(),
  created_at: z.string(),
  from: UserSchema.nullable(),
  id: z.number(),
  included: z
    .object({
      activity: IncludedActivitySchema.optional(),
      appointment: IncludedAppointmentSchema.optional(),
    })
    .optional(),
  message_data: MessageDataSchema.optional(),
  new_lateral_location: LateralLocationSchema.optional(),
  new_location: LocationSchema.optional(),
  previous_lateral_location: LateralLocationSchema.optional(),
  previous_location: LocationSchema.optional(),
  read_at: z.string().nullable().optional(),
  to: UserSchema.nullable(),
  type: MessageType,
});
export type Message = z.infer<typeof MessageSchema>;

const GetConversationMessagesArgsSchema = z.object({
  after: z.number().optional(),
  before: z.number().optional(),
  limit: z.number().optional(),
  patientID: z.number().optional(),
  userID: z.number(),
});
export type GetConversationMessagesArgs = z.infer<typeof GetConversationMessagesArgsSchema>;

export const ConversationMessagesResponseSchema = z.array(MessageSchema);
export type ConversationMessagesResponse = z.infer<typeof ConversationMessagesResponseSchema>;

export const MessagePayloadSchema = z.object({
  activity_id: z.number().optional(),
  body: z.string().min(1),
  to: z.number(),
});
export type MessagePayload = z.infer<typeof MessagePayloadSchema>;

export const BaseResponseSchema = z.object({
  status: z.string(),
});
export type BaseResponse = z.infer<typeof BaseResponseSchema>;

export const PostMessageResponseSchema = z.object({
  ...BaseResponseSchema.shape,
  message: MessageSchema,
});
export type PostMessageResponse = z.infer<typeof PostMessageResponseSchema>;

// BROADCAST MESSAGES

const BroadcastMessageSchema = z.object({
  body: z.string(),
  created_at: z.string(),
  from: UserSchema.nullable(),
  id: z.number().nullable(),
  to: UserSchema.nullable(),
  type: MessageType,
});
export type BroadcastMessage = z.infer<typeof BroadcastMessageSchema>;

const BroadcastFiltersSchema = z.object({
  ce_structure: CeStructureSchema.optional(),
  prio: z.boolean().optional(),
  all: z.boolean().optional(),
});
export type BroadcastFilters = z.infer<typeof BroadcastFiltersSchema>;

const GetBroadcastMessagesArgsSchema = z.object({
  before: z.number().optional(),
  filters: BroadcastFiltersSchema.optional(),
  limit: z.number().optional(),
  userID: z.number(),
});
export type GetBroadcastMessagesArgs = z.infer<typeof GetBroadcastMessagesArgsSchema>;

export const BroadcastMessagesResponseSchema = z.array(BroadcastMessageSchema);
export type BroadcastMessagesResponse = z.infer<typeof BroadcastMessagesResponseSchema>;

export const BroadcastMessagePayloadSchema = z.object({
  body: z.string().min(1),
  filters: BroadcastFiltersSchema.optional(),
});
export type BroadcastMessagePayload = z.infer<typeof BroadcastMessagePayloadSchema>;
